/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, type OnInit, Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { FileUpload } from '@dm/ang';
import { type BaseRequestOptionsArgs, type HttpResponse, type Polling, ActionAbstract, CookieService, HttpService, PollingService, UNPLACEHOLDER } from '@dm/core';
import { ERROR_OK } from 'app/common/constants';
import { PatchService } from 'app/common/services/patch.service';
import { type Observable } from 'rxjs';

import { type UpgradeDetail, PackageTypeE, Progress, UPD_STATUS_E, UPGRADE_QUERY_TYPE, UPLOAD_STATUS } from '../upgrade.const';
import { UpgradeService } from '../upgrade.service';
import { UpgradeStrService } from '../upgrade-str.service';
import { UploadFailDetailComponent } from '../upload-fail-detail/upload-fail-detail.component';

@Component({
    selector: 'app-upgrade-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
})
export class UpgradeFileUploadComponent extends ActionAbstract implements OnInit, AfterViewInit, OnDestroy {
    // 上传包路径  Rest.url
    @Input() restUrl: string;
    // 接受的文件格式、后缀
    @Input() accept: string;
    // 文件大小，Byte
    @Input() maxFileSize: string;
    // 禁用
    @Input() disabled = false;
    // 超时时间, ms
    @Input() timeout: number;
    // 上传文件类型
    @Input() packageType: PackageTypeE;
    // 上传完成后是否获取目标版本
    @Input() getTargetVersion: boolean;
    @Input() placeholder: string;

    // 选中包
    @Output() onFileSelected = new EventEmitter<void>();
    // 开始上传
    @Output() onUploadStart = new EventEmitter<void>();
    // 上传完成
    @Output() onUploadCompelete = new EventEmitter<string>();
    // 上传失败
    @Output() onUploadFailed = new EventEmitter<void>();

    @ViewChild('uploadEl') uploadEl: FileUpload;

    // 上检查项文件路径
    upgradeCheckFileUpload: string;
    // 上传进度轮询
    uploadPolling: Polling;
    // 上传进度
    uploadProgress: number;
    // 上传进度条颜色
    progressColor: string;
    // 上传进度条显示
    uploadProgressShow: boolean;
    // 上传状态显示
    uploadstatus: UPLOAD_STATUS;

    UPLOAD_STATUS = UPLOAD_STATUS;

    currentProcess: Progress;

    uploadMsg = '';

    i18n = this.str.i18n;

    onSelectPatch = this.patchService.onSelectPatch.bind(this);

    updateCheckFile = true;

    uploadOpts: BaseRequestOptionsArgs = {};

    Progress = Progress;

    /**
     * 上传失败详情
     */
    uploadFailDetails: UpgradeDetail[] = [];

    /**
     * 分发阶段失败
     */
    distributeFail = false;

    isJa = this.i18nService.language === 'ja';
    get uploadDisabled(): boolean {
        return this.uploadstatus === UPLOAD_STATUS.distributing;
    }

    constructor(
        injector: Injector,
        public str: UpgradeStrService,
        private cookieService: CookieService,
        private http: HttpService,
        private poll: PollingService,
        private patchService: PatchService,
        private service: UpgradeService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.progressColor = '';
    }

    // 选中要上传文件后回调函数
    onSelect(): void {
        this.uploadProgress = 0;
        this.progressColor = '';
        this.uploadstatus = UPLOAD_STATUS.ing;
        this.uploadProgressShow = false;
        this.distributeFail = false;
        if (this.uploadPolling) {
            this.poll.remove(this.uploadPolling);
        }
        if (this.uploadEl.msgs.length > 0) {
            const msg = this.uploadEl.msgs[0];
            this.uploadMsg = msg.summary;
            if (msg.detail) {
                this.uploadMsg += msg.detail;
            }
            return;
        } else {
            this.uploadMsg = null;
        }
        this.onFileSelected.emit();
    }

    // 向服务端发送数据前回调函数，常用于自定义header
    onBeforeSend(e): void {
        e.xhr.setRequestHeader('Accept-Language', this.str.getLanguage());
        e.xhr.setRequestHeader('iBaseToken', this.cookieService.getToken());
        e.formData.append('iBaseToken', this.cookieService.getToken());
        e.formData.append('Content-type', 'multipart/form-data');
        this.uploadProgressShow = true;
        this.uploadProgress = 0;
    }

    // 文件上传前回调函数，由上传按钮点击触发，常用于自定义POST方法的参数
    onBeforeUpload(e): void {
        e.formData = {
            iBaseToken: this.cookieService.getToken(),
            'Content-type': 'multipart/form-data',
        };
        this.onUploadStart.emit();
    }

    // 上传"成功"，接口返回http 200
    onUpload(e): void {
        // 上传报错
        const error = e.xhr.response && JSON.parse(e.xhr.response).error;
        if (error && Number(error.code) !== ERROR_OK) {
            this.showError(error.description);
            return;
        }

        // 查询分发包进度，0号字段(CMO_EXE_UPD_MSGTYPE)下发数字0
        const rest = this.http.makeUrl('upgrade.uploadProgress').setParams({ CMO_QUERY_TYPE: UPGRADE_QUERY_TYPE.Upload });
        this.uploadPolling = this.poll.add(rest, 5);
        this.uploadPolling.subscribe(({ data }) => {
            this.uploadstatus = UPLOAD_STATUS.distributing;
            this.uploadProgress = Number(data.CMO_UPD_SYS_STEP_STEP_PERCENT);
            this.progressColor = '';
            // 查询上传进度，状态为6时，表示上传补丁包失败
            if (data.CMO_UPD_SYS_STEP_TASK_STATUS === UPD_STATUS_E.UPD_DLD_FAIL) {
                this.doUploadFailed();
                return;
            }

            if (this.uploadProgress === 100) {
                this.uploadstatus = UPLOAD_STATUS.suc;
                if (this.getTargetVersion) {
                    this.getPackageInfo();
                } else {
                    this.onUploadCompelete.emit('');
                }
                this.poll.remove(this.uploadPolling);
            }
        });
    }

    // 上传文件失败
    onError(): void {
        this.progressColor = 'red';
        this.uploadstatus = UPLOAD_STATUS.err;
        this.onUploadFailed.emit();
    }

    doUploadFailed(): void {
        this.poll.remove(this.uploadPolling);
        this.service.getDistributeDetail().subscribe((data: UpgradeDetail[]) => {
            if (!data) {
                return;
            }
            this.showError('');
            this.uploadFailDetails = data;
            this.distributeFail = true;
            this.onUploadFailed.emit();
        });
    }

    // 获取上传包信息
    getPackageInfo(): void {
        const params = {
            CMO_PACKAGE_TYPE: this.packageType,
        };
        this.http.get(this.http.makeUrl('upgrade.packageInfo').setParams(params)).subscribe((result) => {
            this.onUploadCompelete.emit(result.data.CMO_PACKAGE_SPC_VERSION || UNPLACEHOLDER);
        });
    }

    // 查询升级信息
    getUpgradeDetail(): Observable<HttpResponse> {
        const rest = this.http.makeUrl('upgrade.upgradeDetail').setParams({ CMO_QUERY_TYPE: UPGRADE_QUERY_TYPE.Upload });
        return this.http.get(rest, { mask: false });
    }

    showError(errorMsg: string): void {
        this.onError();
        this.uploadMsg = errorMsg;
    }

    /**
     * 打开详情失败详情侧滑框
     */
    showUploadFailDetail(): void {
        this.doAction(UploadFailDetailComponent, {
            data: this.uploadFailDetails,
        });
    }

    ngAfterViewInit(): void {
        if (this.placeholder) {
            this.uploadEl.placeholder = this.placeholder;
        }
    }

    ngOnDestroy(): void {
        if (this.uploadPolling) {
            this.poll.remove(this.uploadPolling);
        }

        // super.ngOnDestroy()?
    }
}
